<template>
    <ais-instant-search
        :search-client="searchClient"
        index-name="pages"
        class="HeaderSearch"
    >
        <ais-configure :hits-per-page.camel="6" />

        <ais-state-results>
            <template v-slot="{ state: { query } }">
                {{ activateSearch(query) }}
            </template>
        </ais-state-results>

        <ais-search-box>
            <template v-slot="{ currentRefinement, refine }">
                <div class="HeaderSearchBox flex-center-b">
                    <input
                        type="search"
                        placeholder="Search the website"
                        :value="currentRefinement"
                        @input="debounceRefine(refine, $event.currentTarget.value)"
                        @keyup.enter="redirectToSearch"
                    >

                    <a :href="searchUrl">
                        <BaseIcon icon="search-icon" />
                    </a>
                </div>
            </template>
        </ais-search-box>

        <HeaderSearchResults v-show="isActive" :searchUrl="searchUrl"/>
    </ais-instant-search>
</template>
<script>
import algoliasearch from 'algoliasearch/lite';
import { debounce } from 'lodash';
import buttonDt from '../assets/images/reciteme_button_dark.png';
import buttonMb from '../assets/images/reciteme_button_mobile.svg';

const algoliaClient = algoliasearch(
    window.algoliaId,
    window.algoliaSearchKey
);

const searchClient = {
    ...algoliaClient,
    search(requests) {
        if (requests.every(({ params }) => !params.query)) {
            return Promise.resolve({
                results: requests.map(() => ({
                    hits: [],
                    nbHits: 0,
                    nbPages: 0,
                    page: 0,
                    processingTimeMS: 0,
                    hitsPerPage: 0,
                    exhaustiveNbHits: false,
                    query: '',
                    params: '',
                })),
            });
        }

        return algoliaClient.search(requests);
    },
};

export default {
    name: 'HeaderSearch',

    data() {
        return {
            searchClient,
            isActive: false,
            searchUrl: '/search'
        }
    },

    methods: {
        activateSearch(q) {
            const queryLength = !!q.length;
            this.isActive = queryLength;
            this.searchUrl = queryLength ? `/search?query=${q}` : '/search';
            document.querySelector('body').classList.toggle('has-overlay', queryLength);
        },

        redirectToSearch() {
            window.location = this.searchUrl;
        },

        debounceRefine: debounce(function (refine, value) {
            window.dataLayer.push({
                event: "header_search_results_update",
                search_keyword: value
            });

            refine(value);
        }, 400)
    },

    mounted() {
        const serviceUrl = "//au-api.reciteme.com/asset/js?key=";
        const serviceKey = "a2d2b2afc8e09f8c78dfb6b4378841ba910fe3fc";
        const options = {};
        const autoLoad = false;
        const enableFragment = "#reciteEnable";
        let loaded = [];
        let frag = false;

        if (window.location.hash === enableFragment) {
            frag = true;
        }

        function loadScript(c, b) {
            const a = document.createElement("script");
            a.type = "text/javascript";
            if (a.readyState) {
                a.onreadystatechange = function () {
                    if (a.readyState === "loaded" || a.readyState === "complete") {
                        a.onreadystatechange = null;
                        if (b !== undefined) b();
                    }
                };
            } else if (b !== undefined) {
                a.onload = function () {
                    b();
                };
            }
            a.src = c;
            document.getElementsByTagName("head")[0].appendChild(a);
        }

        function _rc(c) {
            c += "=";
            const cookies = document.cookie.split(";");
            for (let a = 0; a < cookies.length; a++) {
                let d = cookies[a];
                while (d.charAt(0) === " ") {
                    d = d.substring(1, d.length);
                }
                if (d.indexOf(c) === 0) {
                    return d.substring(c.length, d.length);
                }
            }
            return null;
        }

        function loadService(c) {
            const b = serviceUrl + serviceKey;
            for (let a = 0; a < loaded.length; a++) {
                if (loaded[a] === b) return;
            }
            loaded.push(b);
            loadScript(serviceUrl + serviceKey, function () {
                if (typeof _reciteLoaded === "function") _reciteLoaded();
                if (typeof c === "function") c();
                Recite.load(options);
                Recite.Event.subscribe("Recite:load", function () {
                    Recite.enable();
                });
            });
        }

        if (_rc("Recite.Persist") === "true") {
            loadService();
        }

        if ((autoLoad && _rc("Recite.Persist") !== "false") || frag) {
            if (document.addEventListener) {
                document.addEventListener("DOMContentLoaded", function (c) {
                    loadService();
                });
            } else {
                loadService();
            }
        }

        // Floating Buttons:
        let reciteMeDesktopButton;
        let reciteMeMobileButton;

        function _reciteLoaded() {
            if (reciteMeDesktopButton && reciteMeDesktopButton.parentNode) {
                reciteMeDesktopButton.parentNode.removeChild(reciteMeDesktopButton);
            }
            if (reciteMeMobileButton && reciteMeMobileButton.parentNode) {
                reciteMeMobileButton.parentNode.removeChild(reciteMeMobileButton);
            }
        }

        function _createReciteButtons() {
            // Create Desktop Button
            const desktopContainer = document.createElement('div');
            const desktopImage = document.createElement('img');
            const buttonAlt = 'Recite Me accessibility and Language Support';
            const buttonTitle = 'Launch Recite Me';

            desktopContainer.setAttribute('id', 'reciteme-desktop');
            desktopContainer.setAttribute('class', 'reciteme-button hide-reciteme');
            desktopContainer.setAttribute('alt', buttonAlt);
            desktopContainer.setAttribute('title', buttonTitle);
            desktopImage.setAttribute('alt', buttonAlt);
            desktopImage.setAttribute('title', buttonTitle);
            desktopImage.setAttribute('src', buttonDt);
            desktopContainer.appendChild(desktopImage);

            // Create Mobile Button
            const mobileContainer = document.createElement('div');
            const mobileImage = document.createElement('img');

            mobileContainer.setAttribute('id', 'reciteme-mobile');
            mobileContainer.setAttribute('class', 'reciteme-button hide--reciteme-and-up');
            mobileContainer.setAttribute('alt', buttonAlt);
            mobileContainer.setAttribute('title', buttonTitle);
            mobileImage.setAttribute('alt', buttonAlt);
            mobileImage.setAttribute('title', buttonTitle);
            mobileImage.setAttribute('src', buttonMb);
            mobileContainer.appendChild(mobileImage);

            const desktopParent = document.querySelector('.HeaderSearch');
            const mobileParent = document.querySelector('body');

            if (desktopParent) {
                desktopParent.appendChild(desktopContainer);
            }
            if (mobileParent) {
                mobileParent.appendChild(mobileContainer);
            }

            // Add click handlers
            desktopContainer.addEventListener('click', function () {
                loadService();
                return false;
            });
            mobileContainer.addEventListener('click', function () {
                loadService();
                return false;
            });

            reciteMeDesktopButton = desktopContainer;
            reciteMeMobileButton = mobileContainer;
        }

        _createReciteButtons();
    }
}
</script>

<style lang="scss">

    .HeaderSearch {
        display: flex;
        align-items: center;
        justify-content: between;
        .ais-SearchBox {
            background-color: rgba(255,255,255,.3);
            border-radius: 8px;
            padding: 0 15px
        }
    }


    .DefaultHeader .ais-InstantSearch,
    .TransparentHeader .ais-InstantSearch {
        @include media-query(reciteme) {
            display: none;
        }
    }

    .HeaderSearch--is-transparent {
        color: #fff;

        .HeaderSearchBox input {
            background: transparent;
            color: #fff;

            &::placeholder {
                color: rgba(255, 255, 255, 0.7);
            }
        }

        .TransparentHeader.is-fixed &,
        body.flyout-is-active & {
            color: $base-font-color;
            .HeaderSearchBox input {
                background: transparent;
                color: $base-font-color;

                &::placeholder {
                    color: $base-font-color;
                }
            }
        }
    }

    .HeaderSearchBox input {
        padding: 0.5rem 0;
        border: 0;
        font-size: 1rem;
        color: $base-font-color;
        font-family: $base-font-family;
        font-size: 0.8rem;
        width: 150px;
    }

    .reciteme-button {
        background: #fff;
        cursor: pointer;
        img {
            width: 56px;
            height: 56px;
        }
    }

    #reciteme-mobile {
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2px;
        position: fixed;
        left: 15px;
        bottom: 15px;
        z-index: 3;
    }

    #reciteme-desktop {
        border: 2px solid #0f5780;
        border-radius: 8px;
        color: #0f5780;
        font-size: 16px;
        height: 41.6px;
        margin-left: 20px;
        padding: 0 10px;
        display: flex;
        align-items: center;
        &:before {
            content: 'Accessibility';
        }
        img {
            width: 30px;
            height: 30px;
            margin-left: 10px;
        }
    }

</style>
